import { FC, useCallback, useMemo } from "react";
import { useNewFormsServiceStore } from "@./state";
import Image from "apps/website/components/base/Image/Image";
import Icon from "apps/website/components/base/Icon/Icon";
import Spacer from "apps/website/components/layout/Spacer/Spacer";
import TextControl from "apps/website/components/base/Text/TextControl/TextControl";
import Tag from "apps/website/components/base/Tag/Tag";
import Table from "apps/website/components/feature/Table/Table";

import { DisplayState } from "@/constants/state";

import { LoadingSpinner } from "../../../LoadingSpinner/LoadingSpinner";
import {
  SummaryDiscountCode,
} from "../../SummaryDiscountCode/SummaryDiscountCode";
import { SummaryTH, SummaryTD } from "../table/SummaryTableElements";
import { ISummaryDetailed } from "../types";
import SummaryDetailedHeader from "../components/SummaryDetailedHeader";
import SummaryDetailedPricing from "../components/SummaryDetailedPricing";

const SummaryDetailedRemoveWhyFresh: FC<ISummaryDetailed> = (props) => {
  const { setFlowFieldValue } = useNewFormsServiceStore();
  const { flowSlug, reloadSummary, discounts } = props;

  // This is taken from aother part of the code outside of the test
  const setCatPlanRemoved = useCallback((catId: string | undefined, state: boolean) => {
    if (!catId) {
      console.error("Tried to remove item with no catId", { catId });
      return;
    }
    setFlowFieldValue(flowSlug, "catPlanRemoved", {
      submitValue: state,
      displayValue: state,
    }, catId);
    reloadSummary();
  }, [ setFlowFieldValue, flowSlug, reloadSummary ]);

  const hasChickenSprinkles = useMemo<boolean>(() => (
    !!props.chickenSprinklesData?.quantity
  ), [ props.chickenSprinklesData ]);

  return (
    <div data-component={ SummaryDetailedRemoveWhyFresh.name }>
      { props.image?.src && (
        <Image image={ props.image } alt={ props.title } />
      ) }
      <SummaryDetailedHeader
        discountPercentage={discounts?.discountPercentage}
        trialCostPerDayWithoutDiscount={props.trialCostPerDayWithoutDiscount}
        trialCostPerDayWithDiscount={props.trialCostPerDayWithDiscount}
        hasChickenSprinkles={hasChickenSprinkles}
        itemsLength={props.items.length}
        chickenSprinklesData={props.chickenSprinklesData}
      />

      <Table className={ "w-full border-collapse bg-white" }>

        <>
          { props.state === DisplayState.READY ? (
            <>
              <tr>
                <SummaryTH className="pt-2 lg:pt-4">
                  <TextControl size={ 2 } className="text-green-dark">DPD delivery</TextControl>
                </SummaryTH>
                <SummaryTD className="pt-2 lg:pt-4"><Tag theme="pink" className="p-1">Free</Tag></SummaryTD>
              </tr>
              { props.discounts?.discountPercentage ? (
                <tr>
                  <SummaryTH>
                    <TextControl size={ 2 } className="text-green-dark" whitespace="noWrap">{ props.discounts.discountPercentage }% Discount on fresh food</TextControl>
                  </SummaryTH>
                  <SummaryTD>
                    <TextControl size={ 2 } className="text-green-dark" align="right">-£{ props.discounts.savings.toFixed(2) }</TextControl>
                  </SummaryTD>
                </tr>
              ) : null }
              { props.chickenSprinklesData?.quantity ? (
                <tr>
                  <SummaryTH>
                    <TextControl size={ 2 } className="text-green-dark" whitespace="noWrap">Savings on Chicken Sprinkles</TextControl>
                  </SummaryTH>
                  <SummaryTD>
                    <TextControl size={ 2 } className="text-green-dark" align="right">-£{ props.chickenSprinklesData.savings.toFixed(2) }</TextControl>
                  </SummaryTD>
                </tr>
              ) : null }
              <tr>
                <td colSpan={2} className="px-2 pb-2 lg:px-4 lg:pb-4">
                  <div className="bg-grey-400 h-0.5 w-full"></div>
                </td>
              </tr>
              <SummaryDetailedPricing
                totalTodayNotDiscounted={props.totalTodayNotDiscounted}
                totalTodayDiscounted={props.totalTodayDiscounted}
                chickenSprinklesData={props.chickenSprinklesData}
                deliveryPrice={props.deliveryPrice}
                freshMealPlanPricePerPortion={props.freshMealPlanPricePerPortion}
              />
            </>
          ) : (
            <tr>
              <td colSpan={ 2 } className="flex justify-center items-center py-8">
                <LoadingSpinner color="black" size={ 32 } />
              </td>
            </tr>
          ) }
        </>
      </Table>
      <Spacer size="sm" />
      <div className="flex space-x-2 items-center justify-start">
        <div className="w-[22px] h-[22px]">
          <Icon icon="currency" size="fit" />
        </div>
        <SummaryDiscountCode flowId={ props.flowSlug }
          action="freshPlanSummaryNewCustomer"
          queryParam="discount_code"
          allowReferralCode={ true }
          defaultDiscountCode={ props.defaultDiscountCode }

        />
      </div>
      { props.items.map((item) => (
        <div key={item.catName}>
          { item?.removalData && (item.removalData.planRemoved ? (
            <button onClick={ () => setCatPlanRemoved(item?.removalData?.linkingId, false) }
              className="text-3xs text-green disabled:opacity-30 disabled:cursor-not-allowed min-w-fit underline text-center font-default opacity-100 lg:hover:opacity-70 transition-opacity duration-300">{ item?.removalData?.addTitle }</button>
          ) : <button onClick={ () => setCatPlanRemoved(item?.removalData?.linkingId, true) }
            className="text-3xs text-red disabled:opacity-30 disabled:cursor-not-allowed min-w-fit underline text-center font-default opacity-100 lg:hover:opacity-70 transition-opacity duration-300">{ item?.removalData?.removeTitle }</button>)
          }
        </div>
      )) }
    </div>
  );
};

export default SummaryDetailedRemoveWhyFresh;
